import { useState, useEffect, useMemo } from 'react';
import { getStockAnalysisAllTiers, getDemoTier } from '../lib/cerebro';
import { getStockAudioUrl } from '../lib/oraculoAudio';
import HeroAudioPlayer from './HeroAudioPlayer';
import GlossaryText from './GlossaryText';
import GlossaryHint from './GlossaryHint';
import BullBearCalculator from './BullBearCalculator';
import { ValuationGauge } from './AnalysisVisuals';

// Average historical P/E by sector (approximations)
const HISTORICAL_PE = {
  AAPL: 22, MSFT: 28, NVDA: 35, AMD: 35, TSLA: 80,
  PLTR: 100, HIMS: 40, DUOL: 70,
  VOO: 18, QQQ: 22, SCHD: 17, VWO: 14, SOXX: 25,
};

const TIER_ORDER = ['basic', 'intermediate', 'advanced', 'premium'];

const TIER_META = {
  basic:        { label: 'Básico',     color: '#9CA3AF', unlock: 'Gratis',         minEquity: 0     },
  intermediate: { label: 'Intermedio', color: '#F59E0B', unlock: 'Invierte $1K+',  minEquity: 1000  },
  advanced:     { label: 'Avanzado',   color: '#7C3AED', unlock: 'Invierte $10K+', minEquity: 10000 },
  premium:      { label: 'Premium',    color: '#DC2626', unlock: 'Invierte $50K+', minEquity: 50000 },
};

function tierIndex(t) { return TIER_ORDER.indexOf(t || 'basic'); }

/**
 * StockAnalysis — Tier tabs UI with gamification locking.
 * Calls /api/analysis/stock/:symbol?tier=all to get all 4 tiers in one shot,
 * then renders tabs. Tiers above the user's current tier are locked with an
 * unlock CTA overlay.
 */
export default function StockAnalysis({ symbol }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    if (!symbol) return;
    loadAnalysis();
  }, [symbol]);

  async function loadAnalysis() {
    setLoading(true);
    setError('');
    try {
      const result = await getStockAnalysisAllTiers(symbol);
      setData(result);

      // Default active tab = highest tier the user can access (or demo override)
      const demo = getDemoTier();
      const userTier = demo || result.userTier || 'basic';
      setActiveTab(userTier);
    } catch (err) {
      setError('No se pudo cargar el análisis');
      console.error('[StockAnalysis]', err.message);
    } finally {
      setLoading(false);
    }
  }

  const toggleSection = (key) =>
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // Compute lock state for each tier based on user's tier
  const userTierIdx = useMemo(() => {
    const demo = getDemoTier();
    return tierIndex(demo || data?.userTier || 'basic');
  }, [data]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-3 py-4">
        <div className="h-8 bg-[#F3F4F6] rounded-xl w-full" />
        <div className="h-4 bg-[#F3F4F6] rounded w-3/4" />
        <div className="h-4 bg-[#F3F4F6] rounded w-full" />
        <div className="h-4 bg-[#F3F4F6] rounded w-5/6" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4 text-center">
        <p className="text-xs text-[#9CA3AF]">{error}</p>
        <button onClick={loadAnalysis} className="text-xs text-[#2A59C6] mt-2">
          Reintentar
        </button>
      </div>
    );
  }

  if (!data) return null;

  const activeIdx = tierIndex(activeTab);
  const isLocked = activeIdx > userTierIdx;
  const activeContent = data.tiers?.[activeTab];

  return (
    <div className="py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm">🔮</span>
          <span className="text-sm font-semibold text-[#181D27]">El Oráculo</span>
        </div>
        {data.source === 'ai' && (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
            En vivo
          </span>
        )}
        {data.source === 'cache' && (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
            Actualizado hoy
          </span>
        )}
        {data.source === 'offline' && (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700">
            Pre-generado
          </span>
        )}
      </div>

      {/* Hero Audio — cambia con el tier activo. Solo se muestra si hay audio
          generado para este (symbol, tier). Si el tier esta locked, no aparece. */}
      {!isLocked && getStockAudioUrl(symbol, activeTab) && (
        <HeroAudioPlayer
          src={getStockAudioUrl(symbol, activeTab)}
          tier={activeTab}
          labelHint={symbol}
          kind="stock"
        />
      )}

      {/* Tier tabs */}
      <div className="flex gap-1 mb-3 bg-[#F3F4F6] rounded-xl p-1">
        {TIER_ORDER.map((t, idx) => {
          const meta = TIER_META[t];
          const locked = idx > userTierIdx;
          const active = activeTab === t;
          return (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`flex-1 text-[11px] font-semibold py-2 px-1 rounded-lg transition relative
                ${active ? 'bg-white shadow-sm' : 'hover:bg-white/50'}`}
              style={active ? { color: meta.color } : { color: '#6B7280' }}
            >
              <span className="flex items-center justify-center gap-1">
                {locked && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <rect x="3" y="11" width="18" height="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                )}
                {meta.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Locked tier overlay */}
      {isLocked && (
        <div className="bg-gradient-to-br from-[#F0F4FF] via-[#F5F3FF] to-[#FDF2F8] rounded-xl p-5 mb-3 border border-[#E5E7EB] text-center">
          <div className="text-2xl mb-2">🔒</div>
          <div className="text-sm font-semibold text-[#181D27] mb-1">
            Análisis {TIER_META[activeTab].label} bloqueado
          </div>
          <p className="text-xs text-[#6B7280] leading-relaxed mb-3 max-w-md mx-auto">
            {tierDescriptions[activeTab]}
          </p>
          <div className="inline-block bg-white border border-[#E5E7EB] rounded-lg px-3 py-1.5">
            <span className="text-[11px] text-[#9CA3AF]">Desbloquea: </span>
            <span className="text-[11px] font-semibold" style={{ color: TIER_META[activeTab].color }}>
              {TIER_META[activeTab].unlock}
            </span>
          </div>

          {/* Blurred preview of the locked content */}
          {activeContent && (
            <div className="relative mt-4 max-h-32 overflow-hidden">
              <p className="text-xs text-[#4B5563] leading-relaxed text-left blur-sm select-none">
                {activeContent.substring(0, 400)}…
              </p>
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/30 to-white pointer-events-none" />
            </div>
          )}
        </div>
      )}

      {/* Unlocked content */}
      {!isLocked && activeContent && (
        <>
          <div className="bg-[#F9FAFB] rounded-xl p-4 mb-3">
            <p className="text-sm text-[#4B5563] leading-relaxed whitespace-pre-line">
              <GlossaryText text={activeContent} />
            </p>
          </div>

          {/* Moat — visible desde Avanzado (Premium lo muestra en su deep dive de 10 lentes).
             Lee moatStreet (10 lentes) con moatAnalysis (qualitative) de respaldo. */}
          {activeIdx === 2 && (data.deepDive?.moatStreet || data.qualitative?.moatAnalysis) && (
            <div className="bg-[#FFFBEB] border border-[#FCD34D] rounded-xl p-4 mb-3">
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className="text-sm">🏰</span>
                <span className="text-[11px] uppercase tracking-wider font-semibold text-[#B45309]">El Moat — la ventaja competitiva</span>
              </div>
              <p className="text-xs text-[#78350F] leading-relaxed">
                <GlossaryText text={data.deepDive?.moatStreet || data.qualitative?.moatAnalysis} />
              </p>
            </div>
          )}

          {/* Fundamentals: from intermediate up */}
          {data.fundamentals && activeIdx >= 1 && (
            <CollapsibleSection
              title="Fundamentales"
              icon="📊"
              isOpen={expandedSections.fundamentals}
              onToggle={() => toggleSection('fundamentals')}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <MetricCard label="Precio / Ganancia" shortLabel="P/E" value={data.fundamentals.pe}
                  tooltip="Cuantos años tardarías en recuperar tu inversión si la empresa mantuviera sus ganancias actuales. Mientras mas bajo, mas barata." />
                <MetricCard label="Precio / Ventas" shortLabel="P/S" value={data.fundamentals.ps}
                  tooltip="Cuanto pagas por cada $1 de ventas de la empresa. Útil para empresas que aún no son rentables." />
                <MetricCard label="Rentabilidad" shortLabel="ROE" value={`${data.fundamentals.roe}%`}
                  tooltip="Cuanto gana la empresa por cada $1 invertido por sus dueños. Arriba de 15% es excelente." />
                <MetricCard label="Margen Neto" shortLabel="Margen" value={`${data.fundamentals.netMargin}%`}
                  tooltip="De cada $1 de ventas, cuanto queda como ganancia. Margenes altos = empresa eficiente." />
                <MetricCard label="Dividendo Anual" shortLabel="Dividendo" value={`${data.fundamentals.dividendYield}%`}
                  tooltip="Porcentaje del precio que la empresa te paga al año por tener la acción." />
                <MetricCard label="Tamaño de la empresa" shortLabel="Market Cap" value={data.fundamentals.marketCap}
                  tooltip="Valor total de todas las acciones de la empresa juntas. Indica que tan grande es." />
              </div>
              {data.fundamentals.earningsDate && data.fundamentals.earningsDate !== 'N/A' && (
                <div className="mt-3 text-xs text-[#6B7280] bg-[#F0F4FF] px-3 py-2 rounded-lg">
                  📅 Próximos earnings: <strong>{data.fundamentals.earningsDate}</strong>
                </div>
              )}
              {HISTORICAL_PE[data.symbol] && data.fundamentals.pe && typeof data.fundamentals.pe === 'number' && (
                <ValuationGauge
                  currentPE={data.fundamentals.pe}
                  historicalAvgPE={HISTORICAL_PE[data.symbol]}
                  label="P/E"
                />
              )}
            </CollapsibleSection>
          )}

          {/* Bull/Bear Calculator: from advanced up */}
          {data.scenarios && activeIdx >= 2 && (
            <BullBearCalculator symbol={data.symbol} scenarios={data.scenarios} />
          )}

          {/* Technicals: from advanced up */}
          {data.technicals && activeIdx >= 2 && (
            <CollapsibleSection
              title="Técnico"
              icon="📈"
              isOpen={expandedSections.technicals}
              onToggle={() => toggleSection('technicals')}
            >
              <div className="grid grid-cols-3 gap-2 mb-2">
                <MetricCard label="RSI" value={data.technicals.rsi}
                  highlight={data.technicals.rsi > 70 ? 'red' : data.technicals.rsi < 30 ? 'green' : null} />
                <MetricCard label="SMA 50" value={`$${data.technicals.sma50}`} />
                <MetricCard label="SMA 200" value={`$${data.technicals.sma200}`} />
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-[#6B7280]">Tendencia:</span>
                <TrendBadge trend={data.technicals.trend} />
              </div>
              <div className="flex gap-4 mt-2 text-xs text-[#6B7280]">
                <span>Soporte: ${data.technicals.support}</span>
                <span>Resistencia: ${data.technicals.resistance}</span>
              </div>
            </CollapsibleSection>
          )}

          {/* Deep Dive (Cerebro 2.0 — 10 lentes): premium only */}
          {data.deepDive && data.deepDive.oneLiner && activeTab === 'premium' && (
            <DeepDive2 data={data.deepDive} symbol={symbol} expanded={expandedSections} onToggle={toggleSection} />
          )}

          {/* Legacy qualitative fallback (if deepDive not yet generated) */}
          {!data.deepDive?.oneLiner && data.qualitative && data.qualitative.whatTheySolve && activeTab === 'premium' && (
            <CollapsibleSection
              title="Tesis"
              icon="🔬"
              isOpen={expandedSections.qualitative}
              onToggle={() => toggleSection('qualitative')}
            >
              <div className="space-y-3">
                {data.qualitative.whatTheySolve && <DeepDiveItem title="Qué resuelve" text={data.qualitative.whatTheySolve} />}
                {data.qualitative.moatAnalysis && <DeepDiveItem title="Moat" text={data.qualitative.moatAnalysis} />}
                {data.qualitative.whereTheyreGoing && <DeepDiveItem title="Hacia dónde va" text={data.qualitative.whereTheyreGoing} />}
                {data.qualitative.whyVolatilityDoesntMatter && <DeepDiveItem title="Por qué la volatilidad no importa" text={data.qualitative.whyVolatilityDoesntMatter} />}
                {data.qualitative.management && <DeepDiveItem title="Management" text={data.qualitative.management} />}
                {data.qualitative.catalysts && <DeepDiveItem title="Catalizadores" text={data.qualitative.catalysts} />}
                {data.qualitative.tam && <DeepDiveItem title="TAM" text={data.qualitative.tam} />}
                {data.qualitative.isOptimisationMachine && (
                  <div className="flex items-center gap-2 pt-2 border-t border-[#F3F4F6]">
                    <span className="text-xs">⚡</span>
                    <span className="text-xs font-semibold text-[#059669]">Máquina de Optimización</span>
                  </div>
                )}
              </div>
            </CollapsibleSection>
          )}
        </>
      )}

      {/* Tier missing (no content for this tier even if unlocked) */}
      {!isLocked && !activeContent && (
        <div className="bg-[#F9FAFB] rounded-xl p-4 mb-3 text-center">
          <p className="text-xs text-[#9CA3AF]">
            Análisis {TIER_META[activeTab].label} para {data.symbol} aún no disponible. Vuelve pronto.
          </p>
        </div>
      )}
    </div>
  );
}

const tierDescriptions = {
  intermediate: 'Análisis con fundamentales completos: P/E, márgenes, ROE, dividendo, próximos earnings.',
  advanced:     'Incluye análisis técnico (RSI, SMA, soporte/resistencia), escenarios bull/bear, y proyecciones.',
  premium:      'Deep dive estilo institucional: moat, hacia dónde va, catalizadores, TAM, management y por qué la volatilidad no importa si la tesis es correcta.',
};

// --- Sub-components ---

function CollapsibleSection({ title, icon, isOpen, onToggle, children }) {
  return (
    <div className="border border-[#E5E7EB] rounded-xl mb-2 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#F9FAFB] transition"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm">{icon}</span>
          <span className="text-sm font-medium text-[#181D27]">{title}</span>
        </div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {isOpen && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
}

function MetricCard({ label, shortLabel, value, highlight, tooltip }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const colors = { red: 'text-red-600', green: 'text-green-600' };
  return (
    <div className="bg-[#F9FAFB] rounded-lg p-3 relative">
      <div className="flex items-center justify-between gap-1 mb-1">
        <div className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide">{label}</div>
        {tooltip && (
          <button
            type="button"
            onClick={() => setShowTooltip(!showTooltip)}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            className="text-[#9CA3AF] hover:text-[#2A59C6] transition flex-shrink-0"
            aria-label="Que significa esto?"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </button>
        )}
      </div>
      <div className="flex items-baseline gap-1.5">
        <div className={`text-base font-bold ${highlight ? colors[highlight] : 'text-[#181D27]'}`}>{value || '-'}</div>
        {shortLabel && <div className="text-[10px] text-[#9CA3AF]">{shortLabel}</div>}
      </div>
      {tooltip && showTooltip && (
        <div className="absolute z-10 left-0 right-0 top-full mt-1 mx-1 bg-[#181D27] text-white text-[11px] rounded-lg p-2.5 leading-relaxed shadow-lg">
          {tooltip}
          <div className="absolute -top-1 left-4 w-2 h-2 bg-[#181D27] rotate-45" />
        </div>
      )}
    </div>
  );
}

function TrendBadge({ trend }) {
  const config = {
    bullish: { label: 'Alcista', color: '#059669', bg: '#ECFDF5' },
    bearish: { label: 'Bajista', color: '#DC2626', bg: '#FEF2F2' },
    neutral: { label: 'Neutral', color: '#6B7280', bg: '#F9FAFB' },
  };
  const t = config[trend] || config.neutral;
  return (
    <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: t.bg, color: t.color }}>
      {t.label}
    </span>
  );
}

function DeepDiveItem({ title, text }) {
  return (
    <div>
      <div className="text-xs font-semibold text-[#2A59C6] mb-0.5">{title}</div>
      <p className="text-xs text-[#4B5563] leading-relaxed"><GlossaryText text={text} /></p>
    </div>
  );
}

/**
 * DeepDive2 — Renders the 10-lens Cerebro 2.0 deep dive.
 * Each lens is its own collapsible card. The first lens (oneLiner) is always open
 * and visually highlighted because it's the thesis hook.
 */
function DeepDive2({ data, symbol, expanded, onToggle }) {
  return (
    <div className="mt-2 space-y-2">
      {/* Hint del glosario — solo se muestra mientras el usuario no lo cierre.
          Una vez dismissed, no vuelve a aparecer en ningun lugar del app. */}
      <GlossaryHint />

      {/* Hero — One liner (always visible, no toggle) */}
      <div className="bg-gradient-to-br from-[#0a2540] to-[#1a3a6c] rounded-xl p-4 text-white">
        <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1">La Tesis en una Frase</div>
        <p className="text-sm font-medium leading-relaxed"><GlossaryText text={data.oneLiner} light /></p>
      </div>

      {/* Clarity test — also highly visible */}
      {data.clarityTest && (
        <div className="bg-[#F0FDF4] border border-[#86EFAC] rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-xs">✅</span>
            <span className="text-[10px] uppercase tracking-wider font-semibold text-[#059669]">Test de Claridad</span>
          </div>
          <p className="text-xs text-[#14532D] leading-relaxed"><GlossaryText text={data.clarityTest} /></p>
        </div>
      )}

      {/* The rest as collapsible cards */}
      {data.analogy && (
        <LensCard k="analogy" title="¿Qué resuelve, en analogía?" icon="🪟" text={data.analogy} expanded={expanded} onToggle={onToggle} />
      )}

      {data.moatStreet && (
        <LensCard
          k="moat"
          title="El Moat — la calle que nadie puede cruzar"
          icon="🏰"
          text={data.moatStreet}
          expanded={expanded}
          onToggle={onToggle}
        />
      )}

      {data.adoptionCurve && data.adoptionCurve.today && (
        <CollapsibleSection
          title="¿Dónde estamos en la curva de adopción?"
          icon="📈"
          isOpen={expanded.adoption}
          onToggle={() => onToggle('adoption')}
        >
          <div className="space-y-2">
            <DeepDiveItem title="Hoy" text={data.adoptionCurve.today} />
            <DeepDiveItem title="Horizonte 2030+" text={data.adoptionCurve.horizon} />
            <DeepDiveItem title="Ley de Costos" text={data.adoptionCurve.wrightsLaw} />
          </div>
        </CollapsibleSection>
      )}

      {data.macroChain && (
        <LensCard k="macro" title="Cadena macro → tesis" icon="🌎" text={data.macroChain} expanded={expanded} onToggle={onToggle} />
      )}

      {data.reinforcingPlatforms && data.reinforcingPlatforms.length > 0 && (
        <CollapsibleSection
          title="Plataformas que se refuerzan"
          icon="🔗"
          isOpen={expanded.platforms}
          onToggle={() => onToggle('platforms')}
        >
          <ul className="space-y-1.5">
            {data.reinforcingPlatforms.map((p, i) => (
              <li key={i} className="text-xs text-[#4B5563] leading-relaxed flex gap-2">
                <span className="text-[#7C3AED] font-bold flex-shrink-0">→</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {data.bearCaseSteelman && data.bearCaseSteelman.argument && (
        <CollapsibleSection
          title="Bear Case Steelman"
          icon="🐻"
          isOpen={expanded.bear}
          onToggle={() => onToggle('bear')}
        >
          <div className="space-y-3">
            <div>
              <div className="text-xs font-semibold text-[#DC2626] mb-0.5">El argumento en contra</div>
              <p className="text-xs text-[#4B5563] leading-relaxed">{data.bearCaseSteelman.argument}</p>
            </div>
            <div className="border-t border-[#F3F4F6] pt-2">
              <div className="text-xs font-semibold text-[#059669] mb-0.5">Refutación honesta</div>
              <p className="text-xs text-[#4B5563] leading-relaxed">{data.bearCaseSteelman.rebuttal}</p>
            </div>
          </div>
        </CollapsibleSection>
      )}

      {data.preMortem && (
        <LensCard k="premortem" title="Pre-Mortem — Es 2031, subió 10x" icon="🔮" text={data.preMortem} expanded={expanded} onToggle={onToggle} />
      )}

      {data.volatilityTest && (
        <LensCard k="volatility" title="Stress test de volatilidad" icon="🎢" text={data.volatilityTest} expanded={expanded} onToggle={onToggle} />
      )}

      {/* Footer with generation metadata */}
      {data.generatedAt && (
        <div className="text-[10px] text-[#9CA3AF] text-right mt-2">
          Generado el {new Date(data.generatedAt).toLocaleDateString('es-CO')}
          {data.modelUsed && ` · ${data.modelUsed.replace('claude-', '')}`}
        </div>
      )}
    </div>
  );
}

function LensCard({ k, title, icon, text, audioUrl, expanded, onToggle }) {
  return (
    <CollapsibleSection title={title} icon={icon} isOpen={expanded[k]} onToggle={() => onToggle(k)}>
      <p className="text-xs text-[#4B5563] leading-relaxed whitespace-pre-line">
        <GlossaryText text={text} />
      </p>
      {audioUrl && <AudioPlayer src={audioUrl} />}
    </CollapsibleSection>
  );
}

/**
 * AudioPlayer — Reproductor HTML5 nativo para narraciones del Oraculo.
 * Aparece debajo del texto de la lente cuando hay un audio disponible en S3.
 * Usa controlsList="nodownload" para evitar que usuarios descarguen el .mp3 directo.
 */
function AudioPlayer({ src }) {
  if (!src) return null;
  return (
    <div className="mt-3 pt-3 border-t border-[#F3F4F6]">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-[#0a2540] mb-1.5">
        Escuchar narrado por el Oráculo
      </div>
      <audio
        controls
        controlsList="nodownload"
        src={src}
        className="w-full h-8"
        preload="metadata"
      />
    </div>
  );
}

// HeroAudioPlayer ahora vive en ./HeroAudioPlayer.jsx (compartido con Investments.jsx)
